import {
  addDoc,
  deleteDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { track } from "@/lib/analytics";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol, refDoc } from "./paths";
import {
  choreSchema,
  type Chore,
  type ChoreInput,
} from "@/lib/schemas";

export async function listChores(
  hid: string,
  opts: { onlyActive?: boolean; assignedTo?: string } = {},
): Promise<Chore[]> {
  const db = getDb();
  const constraints = [];
  if (opts.onlyActive) constraints.push(where("active", "==", true));
  if (opts.assignedTo)
    constraints.push(
      where("assignedTo", "in", [opts.assignedTo, "any", "all-children"]),
    );
  const q =
    constraints.length > 0
      ? query(refCol(db, paths.chores(hid)), ...constraints)
      : query(refCol(db, paths.chores(hid)), orderBy("title"));
  const snap = await getDocs(q);
  return snap.docs.flatMap((d) => {
    const parsed = choreSchema.safeParse({ id: d.id, ...d.data() });
    return parsed.success ? [parsed.data] : [];
  });
}

export async function createChore(
  hid: string,
  uid: string,
  input: ChoreInput,
): Promise<string> {
  const db = getDb();
  const ref = await addDoc(refCol(db, paths.chores(hid)), {
    ...input,
    createdBy: uid,
    createdAt: serverTimestamp(),
  });
  track("chore_created", {
    kind: input.kind,
    offline: input.offline,
    requiresPhoto: input.requiresPhoto ?? false,
  });
  return ref.id;
}

export async function updateChore(
  hid: string,
  cid: string,
  patch: Partial<ChoreInput>,
): Promise<void> {
  const db = getDb();
  await updateDoc(refDoc(db, paths.chore(hid, cid)), patch);
}

export async function deactivateChore(hid: string, cid: string): Promise<void> {
  const db = getDb();
  await updateDoc(refDoc(db, paths.chore(hid, cid)), { active: false });
}

export async function deleteChore(hid: string, cid: string): Promise<void> {
  const db = getDb();
  await deleteDoc(refDoc(db, paths.chore(hid, cid)));
}
